import type { UDFBarData, TradeObject, SymbolInfo } from './types'

let tradeSequence = 0

type AggregationLike =
  | string
  | {
      type?: string
      duration?: number
      durationType?: string
    }
  | null
  | undefined

function normalizeAggregationKey(aggregation: AggregationLike): string {
  if (!aggregation) {
    return ''
  }

  if (typeof aggregation === 'string') {
    return aggregation.trim()
  }

  if (typeof aggregation.type === 'string' && aggregation.type.trim()) {
    return aggregation.type.trim()
  }

  if (typeof aggregation.duration === 'number' && typeof aggregation.durationType === 'string') {
    const durationType = aggregation.durationType.toLowerCase()
    if (['m', 'h', 'd', 'w', 'mo'].includes(durationType)) {
      return `${aggregation.duration}${durationType}`
    }
  }

  return ''
}

export function translateIntervalForAPI(aggregation: AggregationLike): string {
  const interval = normalizeAggregationKey(aggregation)
  const map: Record<string, string> = {
    // Short-form wire contract (backend accepts both short and long form)
    '1m': '1m',
    '5m': '5m',
    '15m': '15m',
    '30m': '30m',
    '1h': '1h',
    '2h': '2h',
    '4h': '4h',
    '1d': '1d',
    '1D': '1d',
    '1W': '1w',
    '1M': '1mo',

    // Long-form inputs also map to short-form output
    '1min': '1m',
    '5min': '5m',
    '15min': '15m',
    '30min': '30m',
    '1day': '1d',
    '1week': '1w',
    '1month': '1mo',

    // New dxchart aggregation model (duration + durationType)
    '1w': '1w',
    '1mo': '1mo',
  }

  return map[interval] || ''
}

export function transformBarsFromAPI(bars: any[]): any[] {
  return bars.map((bar) => ({
    ...bar,
    time: new Date(bar.timestamp).getTime(),
  }))
}

export function convertToUDFFormat(rawBars: any[]): UDFBarData {
  if (!rawBars || rawBars.length === 0) {
    return {
      s: 'no_data',
      nextTime: null,
      t: [],
      o: [],
      h: [],
      l: [],
      c: [],
      v: [],
    }
  }

  const t: number[] = []
  const o: number[] = []
  const h: number[] = []
  const l: number[] = []
  const c: number[] = []
  const v: number[] = []

  rawBars.forEach((bar) => {
    t.push(Math.floor(bar.time / 1000))
    o.push(Number(bar.open))
    h.push(Number(bar.high))
    l.push(Number(bar.low))
    c.push(Number(bar.close))
    v.push(Number(bar.volume || 0))
  })

  return { s: 'ok', t, o, h, l, c, v }
}

export function parseSymbolName(fullName: string): {
  exchange: string
  segment: string
  symbol: string
} {
  const parts = fullName.split(':')
  return {
    exchange: parts[0] || '',
    segment: parts[1] || '',
    symbol: parts[2] || '',
  }
}

export function createTradeFromPriceUpdate(symbolFullName: string, price: number): TradeObject {
  const { exchange, segment, symbol } = parseSymbolName(symbolFullName)

  const timestamp = Date.now()
  const sequence = ++tradeSequence
  const quantity = 0
  const tradeID = `ws_trade_${symbol}_${timestamp}_${sequence}`
  const amount = price * quantity

  return {
    type: 'trade',
    side: 'BUY',
    timestamp,
    price,
    quantity,
    tradeID,
    productId: symbolFullName,
    symbol,
    exchange,
    segment,
    timeStamp: new Date(timestamp),
    amount,
  }
}

export function symbolInfoFullName(symbolInfo: SymbolInfo): string {
  return `${symbolInfo.exchange}:${symbolInfo.segment}:${symbolInfo.symbol}`
}

export function resolveMappedShortSymbol(
  symbol: string | null | undefined,
  symbolFullNames: Record<string, string>,
  symbolShortNames: Record<string, string>,
): string | undefined {
  if (!symbol) {
    return undefined
  }

  const trimmedSymbol = symbol.trim()
  if (!trimmedSymbol) {
    return undefined
  }

  if (symbolFullNames[trimmedSymbol]) {
    return trimmedSymbol
  }

  const shortFromFullName = symbolShortNames[trimmedSymbol]
  if (shortFromFullName) {
    return shortFromFullName
  }

  const slashlessSymbol = trimmedSymbol.replace(/\//g, '').toUpperCase()
  return Object.keys(symbolFullNames).find((shortName) => shortName.replace(/\//g, '').toUpperCase() === slashlessSymbol) || trimmedSymbol
}

export function resolveMappedFullSymbol(
  symbol: string | null | undefined,
  symbolFullNames: Record<string, string>,
  symbolShortNames: Record<string, string>,
): string | undefined {
  const shortSymbol = resolveMappedShortSymbol(symbol, symbolFullNames, symbolShortNames)
  if (!shortSymbol) {
    return undefined
  }

  return symbolFullNames[shortSymbol] || symbol || shortSymbol
}
